import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import EstoqueConciliacaoService, { IEstoqueAnaliseEsteira, IEstoquePosicaoGrupo } from '../../services/EstoqueConciliacaoService';

import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    show: boolean;
    onHide: () => void;
    esteiraId: number;
    /** Quando informada, o modal mostra só a decisão e os grupos desta conta (aberto a partir do cartão dela). Sem ela, mostra a esteira inteira (comportamento antigo). */
    contaEmpresa?: string;
}

const REASON_COR: Record<string, string> = {
    CONCILIADO: 'bg-success',
    CONCILIADO_COM_ARREDONDAMENTO: 'bg-success',
    DIVERGENCIA_DE_SALDO: 'bg-danger',
    POSSIVEL_CLASSIFICACAO_INDEVIDA: 'bg-warning text-dark',
    INCONSISTENCIA_NA_ORIGEM_ESTOQUE: 'bg-danger',
    SALDO_ESTOQUE_AUSENTE: 'bg-secondary',
};

const REASON_LABEL: Record<string, string> = {
    CONCILIADO: 'Conciliado',
    CONCILIADO_COM_ARREDONDAMENTO: 'Conciliado (arredondamento)',
    DIVERGENCIA_DE_SALDO: 'Divergência de saldo',
    POSSIVEL_CLASSIFICACAO_INDEVIDA: 'Possível classificação indevida',
    INCONSISTENCIA_NA_ORIGEM_ESTOQUE: 'Inconsistência na origem',
    SALDO_ESTOQUE_AUSENTE: 'Sem dado de estoque',
};

const fmt = (v: number | null | undefined) =>
    v == null ? '—' : v.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

const ModalEstoqueAnalise = ({ show, onHide, esteiraId, contaEmpresa }: Props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [analise, setAnalise] = useState<IEstoqueAnaliseEsteira | null>(null);
    const [expandida, setExpandida] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!esteiraId) return;
        setLoading(true);
        try {
            setAnalise(await EstoqueConciliacaoService.buscarAnalise(esteiraId));
        } finally {
            setLoading(false);
        }
    }, [esteiraId]);

    useEffect(() => {
        if (show) {
            // Já abre expandida na conta do cartão que disparou o modal — o usuário não precisa clicar
            // pra ver a quebra por grupo daquela conta.
            setExpandida(contaEmpresa ?? null);
            load();
        }
    }, [show, load, contaEmpresa]);

    const gruposDaConta = (conta: string): IEstoquePosicaoGrupo[] =>
        analise?.grupos.filter(g => g.contaContabil === conta) ?? [];

    // Sem contaEmpresa, mantém o comportamento antigo (esteira inteira, todas as contas de estoque).
    const decisoesExibidas = contaEmpresa
        ? (analise?.decisoes.filter(d => d.contaEmpresa === contaEmpresa) ?? [])
        : (analise?.decisoes ?? []);
    const decisaoConta = contaEmpresa ? decisoesExibidas[0] : undefined;

    return (
        <Modal size='xl' centered scrollable show={show} onHide={onHide}>
            <div className="modal-content" style={{ backgroundColor: '#F4F5F7' }}>
                <div className="modal-header border-0 pb-1">
                    <div>
                        <h5 className="modal-title fw-bold mb-0" style={{ textTransform: 'uppercase', color: '#5E6397' }}>
                            {t('Conciliação de Estoque')}
                        </h5>
                        {analise && (
                            <small className="text-muted">
                                {analise.hotel} {analise.competencia ? `· ${analise.competencia}` : ''}
                                {contaEmpresa && ` · ${t('Conta')} ${contaEmpresa}`}
                            </small>
                        )}
                    </div>
                    <button className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={onHide}>
                        <i className="fas fa-times" style={{ color: '#5E6397' }} />
                    </button>
                </div>

                {loading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <span className="spinner-border spinner-border-sm me-2 text-primary" />
                        <span className="text-muted">{t('Carregando...')}</span>
                    </div>
                )}

                {!loading && !analise && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                        <i className="fas fa-circle-exclamation fa-3x mb-3" style={{ color: '#ccc' }} />
                        <p className="mb-0">{t('Esta esteira ainda não tem conciliação de Estoque processada.')}</p>
                    </div>
                )}

                {!loading && analise && contaEmpresa && decisoesExibidas.length === 0 && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                        <i className="fas fa-circle-exclamation fa-3x mb-3" style={{ color: '#ccc' }} />
                        <p className="mb-0">{t('Esta conta ainda não tem conciliação de Estoque processada.')}</p>
                    </div>
                )}

                {!loading && analise && (contaEmpresa ? decisoesExibidas.length > 0 : true) && (
                    <>
                        <div className="px-4 pt-3" style={{ backgroundColor: '#fff' }}>
                            {decisaoConta ? (
                                // Escopo de 1 conta (aberto a partir do cartão) — números da própria conta, não da esteira.
                                <div className="row g-3 pb-3">
                                    <div className="col-md-4">
                                        <div className="border rounded p-3 h-100 text-center">
                                            <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#5E6397' }}>{fmt(decisaoConta.saldoContabil)}</div>
                                            <small className="text-muted text-uppercase">{t('Saldo Contábil')}</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="border rounded p-3 h-100 text-center">
                                            <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#5E6397' }}>{fmt(decisaoConta.saldoEstoque)}</div>
                                            <small className="text-muted text-uppercase">{t('Saldo Estoque')}</small>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="border rounded p-3 h-100 text-center">
                                            <div className={`fw-bold ${decisaoConta.diferenca != null && Math.abs(decisaoConta.diferenca) > 0.05 ? 'text-danger' : ''}`} style={{ fontSize: '1.2rem', color: '#5E6397' }}>{fmt(decisaoConta.diferenca)}</div>
                                            <small className="text-muted text-uppercase">{t('Diferença')}</small>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="row g-3 pb-3">
                                    <div className="col-md-3">
                                        <div className="border rounded p-3 h-100 text-center">
                                            <div className="fw-bold" style={{ fontSize: '1.4rem', color: '#5E6397' }}>{analise.totalConciliadas}/{analise.totalContas}</div>
                                            <small className="text-muted text-uppercase">{t('Contas conciliadas')}</small>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border rounded p-3 h-100 text-center">
                                            <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#5E6397' }}>{fmt(analise.saldoEstoqueTotal)}</div>
                                            <small className="text-muted text-uppercase">{t('Saldo estoque (soma)')}</small>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border rounded p-3 h-100 text-center">
                                            <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#5E6397' }}>{fmt(analise.saldoContabilTotal)}</div>
                                            <small className="text-muted text-uppercase">{t('Saldo contábil (soma)')}</small>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="border rounded p-3 h-100 text-center">
                                            <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#5E6397' }}>{fmt(analise.divergenciaBrutaTotal)}</div>
                                            <small className="text-muted text-uppercase">{t('Divergência bruta (informativo)')}</small>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="alert alert-light border py-2 px-3 mb-3" style={{ fontSize: '0.8rem' }}>
                                <i className="fas fa-circle-info me-2" style={{ color: '#5E6397' }} />
                                {t('A divergência bruta é só um total informativo — cada conta é conciliada individualmente e nunca é compensada pelo resultado geral, mesmo quando a soma acima está próxima de zero.')}
                            </div>
                        </div>

                        <div className="px-4 pb-4" style={{ backgroundColor: '#fff' }}>
                            <div className="table-responsive">
                                <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-3">
                                    <thead>
                                        <tr className="fw-bold text-muted bg-light">
                                            <th style={{ width: 30 }}></th>
                                            <th className="min-w-120px ps-2">{t('Conta')}</th>
                                            <th className="min-w-100px text-end">{t('Saldo Contábil')}</th>
                                            <th className="min-w-100px text-end">{t('Saldo Estoque')}</th>
                                            <th className="min-w-100px text-end">{t('Diferença')}</th>
                                            <th className="min-w-170px">{t('Resultado')}</th>
                                            <th className="min-w-110px">{t('Conta Suspeita')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {decisoesExibidas.map((d) => {
                                            const aberta = expandida === d.contaEmpresa;
                                            const grupos = gruposDaConta(d.contaEmpresa);
                                            return (
                                                <React.Fragment key={d.id}>
                                                    <tr style={{ cursor: grupos.length ? 'pointer' : 'default' }}
                                                        onClick={() => grupos.length && setExpandida(aberta ? null : d.contaEmpresa)}>
                                                        <td className="text-center text-muted">
                                                            {grupos.length > 0 && <i className={`fas fa-chevron-${aberta ? 'down' : 'right'}`} />}
                                                        </td>
                                                        <td className="ps-2">
                                                            <span className="fw-bold">{d.contaEmpresa}</span>
                                                            {d.descricao && <div className="text-muted" style={{ fontSize: '0.78rem' }}>{d.descricao}</div>}
                                                            {d.temInconsistenciaOrigem && (
                                                                <span className="badge bg-light text-danger border border-danger mt-1" style={{ fontSize: '0.65rem' }}>
                                                                    <i className="fas fa-triangle-exclamation me-1" />{t('Equação de estoque não fecha')}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="text-end">{fmt(d.saldoContabil)}</td>
                                                        <td className="text-end">{fmt(d.saldoEstoque)}</td>
                                                        <td className={`text-end fw-bold ${d.diferenca && Math.abs(d.diferenca) > 0.05 ? 'text-danger' : ''}`}>{fmt(d.diferenca)}</td>
                                                        <td><span className={`badge ${REASON_COR[d.reasonCode] ?? 'bg-secondary'}`}>{t(REASON_LABEL[d.reasonCode] ?? d.reasonCode)}</span></td>
                                                        <td>
                                                            {d.contaSuspeita
                                                                ? <span title={fmt(d.valorClassificacaoIndevida)}>{d.contaSuspeita} <span className="text-muted">({fmt(d.valorClassificacaoIndevida)})</span></span>
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                    {aberta && (
                                                        <tr>
                                                            <td></td>
                                                            <td colSpan={6} className="pt-0">
                                                                {d.orientacao && (
                                                                    <div className="alert alert-light border py-2 px-3 mb-2" style={{ fontSize: '0.78rem' }}>
                                                                        <i className="fas fa-lightbulb me-2" style={{ color: '#5E6397' }} />{d.orientacao}
                                                                    </div>
                                                                )}
                                                                <div className="table-responsive">
                                                                    <table className="table table-sm table-bordered align-middle mb-1" style={{ fontSize: '0.76rem' }}>
                                                                        <thead className="bg-light text-muted">
                                                                            <tr>
                                                                                <th>{t('Grupo')}</th>
                                                                                <th className="text-end">{t('Saldo Inicial')}</th>
                                                                                <th className="text-end">{t('Receb. Forn.')}</th>
                                                                                <th className="text-end">{t('Devol. Forn.')}</th>
                                                                                <th className="text-end">{t('Entrada Transf.')}</th>
                                                                                <th className="text-end">{t('Baixa Transf.')}</th>
                                                                                <th className="text-end">{t('Baixa Acerto Inv.')}</th>
                                                                                <th className="text-end">{t('Baixa Perda')}</th>
                                                                                <th className="text-end">{t('Baixa C.Custo')}</th>
                                                                                <th className="text-end">{t('Saldo Atual')}</th>
                                                                                <th className="text-end">{t('Δ equação')}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {grupos.map((g) => (
                                                                                <tr key={g.codGrupo}>
                                                                                    <td>{g.codGrupo} {g.descGrupo}</td>
                                                                                    <td className="text-end">{fmt(g.saldoInicial)}</td>
                                                                                    <td className="text-end">{fmt(g.recebimentoFornecedor)}</td>
                                                                                    <td className="text-end">{fmt(g.devolucaoFornecedor)}</td>
                                                                                    <td className="text-end">{fmt(g.entradaTransferencia)}</td>
                                                                                    <td className="text-end">{fmt(g.baixaTransferencia)}</td>
                                                                                    <td className="text-end">{fmt(g.baixaAcertoInventario)}</td>
                                                                                    <td className="text-end">{fmt(g.baixaPerdaEstrago)}</td>
                                                                                    <td className="text-end">{fmt(g.baixaCentroCusto)}</td>
                                                                                    <td className="text-end fw-bold">{fmt(g.saldoAtual)}</td>
                                                                                    <td className={`text-end ${Math.abs(g.diferencaEquacao) > 0.01 ? 'text-danger fw-bold' : 'text-muted'}`}>{fmt(g.diferencaEquacao)}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.75rem' }}>
                                <i className="fas fa-magnifying-glass me-2" />
                                {t('A investigação por item, documento e movimento (nível de confiança) faz parte da Fase 5 e ainda não está disponível.')}
                            </p>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ModalEstoqueAnalise;
